import {
    Inject,
    Injectable,
    InjectionToken,
    isDevMode,
    Provider,
} from '@angular/core';
import {
    NavigationEnd,
    Router,
} from '@angular/router';
import { AppStateFacade } from '@app-state';
import { AuthService } from '@services/auth.service';

import {
    BehaviorSubject,
    Observable,
} from 'rxjs';
import {
    distinctUntilChanged,
    distinctUntilKeyChanged,
    filter,
    map,
    startWith,
    tap,
} from 'rxjs/operators';

import {
    convertToCategory,
    IPageMetadata,
    ISidebarSection,
    PageCategory,
    PageMetadata,
    PageScope,
    SIDEBAR_SECTION_METADATA,
    SIDEBAR_SECTION_TYPES,
} from './page-metadata';

export const ORCHESTRATOR_PAGE_META = new InjectionToken<IPageMetadata>('Orchestrator Page Metadata');

export function providePageMeta(page: IPageMetadata): Provider {
    return {
        provide: ORCHESTRATOR_PAGE_META,
        useValue: page,
        multi: true,
    };
}

const DEFAULT_PAGE = new PageMetadata({
    category: NaN,
    title: '',
    translationKey: '',
    routerLink: '',
    scope: PageScope.Global,
});

const priorityComparer = (a: PageMetadata, b: PageMetadata) => a.priority - b.priority;

const mapPageMeta = (page: IPageMetadata, _idx: number, pages: IPageMetadata[]) => {
    const parent = pages.find(p => p.routerLink === page.parentRoute);

    if (!parent && !!page.parentRoute) {
        throw new Error(`Could not find parent '${page.parentRoute}' for page: '${page.title}'`);
    }

    return new PageMetadata(page, parent);
};

const cleanUrl = (url: string) => url.split('?').shift()!;

@Injectable()
export class PageMetadataRegistry {
    public get activePage(): Readonly<PageMetadata | undefined> {
        return this._activePage$.value;
    }

    public get commonPages() {
        return this._pageMap.get(PageCategory.All)!;
    }

    public get sidebarSections(): ISidebarSection[] {
        const sidebarPages = this._enabledUserPages
            .filter(page => page.type === 'Sidebar');

        if (this._appState.user.loggedInAsHost) {
            return [{ pages: sidebarPages.sort(priorityComparer) }];
        }

        return SIDEBAR_SECTION_TYPES.map(section => ({
            name: section,
            metadata: SIDEBAR_SECTION_METADATA[section],
            pages: sidebarPages.filter(p => p.sidebarSection === section).sort(priorityComparer),
        }));
    }

    public get userMenuPages() {
        return this._enabledUserPages
            .filter(page => page.type === 'Admin')
            .sort(priorityComparer);
    }

    public get cleanCurrentUrl() {
        return cleanUrl(this._router.url);
    }

    public readonly activePage$: Observable<PageMetadata | undefined>;

    public get pages(): Readonly<PageMetadata[]> {
        return this._pages;
    }

    private _pageMap: Map<PageCategory, PageMetadata[]> = new Map();
    private _pages: PageMetadata[] = [];
    private _activePage$ = new BehaviorSubject<PageMetadata | undefined>(DEFAULT_PAGE);

    private get _enabledUserPages() {
        return this._userPages.filter(page =>
            !page.featureFlag ||
            this._appState.settings.isFeatureEnabled(page.featureFlag),
        );
    }

    private get _userPages() {
        const state = this._auth.pendingLogout$.value
            ? PageCategory.Unauthorized :
            convertToCategory(this._appState.user.authState);

        return this._pageMap.get(state)!;
    }

    constructor(
        @Inject(ORCHESTRATOR_PAGE_META)
        pages: IPageMetadata[],
        private _appState: AppStateFacade,
        private _router: Router,
        // FIXME: remove after identity server is implemented
        private _auth: AuthService,
    ) {
        this.activePage$ = this._activePage$
            .asObservable();

        this._pages = pages
            .map(mapPageMeta);

        this._pages
            .forEach(page => {
                if (!this._pageMap.has(page.category)) {
                    this._pageMap.set(page.category, []);
                }

                this._pageMap.get(page.category)!.push(page);
            });

        this.activePage$.subscribe(page => {
            this._appState.user.folderPermissions.flushCache();

            if (isDevMode()) {
                console.log('Page Registry Active Page: ', page);
            }
        });
    }

    public listen() {
        this._router
            .events
            .pipe(
                filter(ev =>
                    ev instanceof NavigationEnd ||
                    // restricted access page will not generate NavigationEnd, but NavigationCancel
                    this._router.url.startsWith('/restricted-access'),
                ),
                startWith(void 0),
                map(() => this.pageForCurrentRoute()),
                map(page => {
                    if (!page) { throw new Error(`No metadata was registered for '${this._router.url}'.`); }

                    return page!;
                }),
                distinctUntilKeyChanged('routerLink'),
                tap(() => this.setCustomTitle('')),
            ).subscribe(page => this._activePage$.next(page));
    }

    public find(id: string) {
        return this.pages.find(p => p.identifier === id);
    }

    public setCustomTitle(value: string) {
        (this.activePage as any).customTitle = value;
    }

    public findByTitle(title: string) {
        return this.pages.find(p => p.title === title);
    }

    public isActivePage({ cleanRouterLink }: PageMetadata) {
        return this.activePage$.pipe(
            map(activePage =>
                activePage && cleanRouterLink === activePage.cleanRouterLink,
            ),
            distinctUntilChanged(),
        );

    }

    public hasActivePage({ cleanRouterLink }: PageMetadata) {
        return this.activePage$.pipe(
            map(activePage =>
                activePage &&
                (
                    cleanRouterLink === activePage.cleanRouterLink ||
                    activePage.parent &&
                    activePage.parent.cleanRouterLink === cleanRouterLink
                ),
            ),
            distinctUntilChanged(),
        );
    }

    public findByRoute(link: string, searchAllTargets = false) {
        const cleanLink = cleanUrl(link);

        let result = this._userPages.find(page => page.isMatch(cleanLink));

        if (!result && searchAllTargets) {
            result = this._pages.find(page => page.isMatch(cleanLink));
        }

        if (!result) {
            result = this._pageMap.get(PageCategory.All)!
                .find(page => page.isMatch(cleanLink));
        }

        return result;
    }

    public pageForCurrentRoute() {
        return this.findByRoute(this.cleanCurrentUrl, this._appState.authConfig.value.auth.isCustomSsoEnabled);
    }
}
